import {graphql} from 'gatsby'
import React from 'react'
import styled, {css} from 'styled-components'

import Button from '@/components/button'
import Carousel from '@/components/carousel'
import Image from '@/components/image'
import PrismicLink from '@/components/prismic-link'
import RichText from '@/components/rich-text'
import {Text} from '@/components/text'
import {Video} from '@/components/video'
import {ArrowLeft} from '@/images/icons/arrow-left'
import {ArrowRight} from '@/images/icons/arrow-right'
import {BackgroundWrapper, Section} from '@/styles/grid'
import {tokens} from '@/theme'
import mq from '@/utils/media-query'

import type {RefType as CarouselRefType} from '@/components/carousel'
import type {Props as PrismicLinkProps} from '@/components/prismic-link'
import type {Props as RichTextProps} from '@/components/rich-text'
import type {colors} from '@/theme'
import type {ImageSource} from '@/types/image'
import type {InlineVideo} from '@/types/inline-video'

interface Item {
    title: string
    body: RichTextProps['content']
    ctaText: string
    ctaLink: PrismicLinkProps
    image?: ImageSource
    video?: InlineVideo
}

const allowedColors = ['muted', 'yellow', 'pink', 'purple'] as const

type BackgroundColor = keyof Pick<typeof colors.background, (typeof allowedColors)[number]>

export const isAllowedBackgroundColor = (value: string): value is BackgroundColor =>
    allowedColors.some((allowedColor) => allowedColor === value)

export interface Props {
    title?: string
    mirrored?: boolean
    highlightColor?: BackgroundColor
    items: Item[]
}

export const Highlight = ({mirrored, title, items, highlightColor = 'muted'}: Props) => {
    const ref = React.useRef<CarouselRefType>(null)

    return (
        <OuterWrapper>
            {title && (
                <Text as="h2" $variant="heading-base">
                    {title}
                </Text>
            )}
            {items.length === 1 ? (
                <SlideContent
                    {...items[0]}
                    index={0}
                    itemsLength={items.length}
                    mirrored={mirrored}
                    highlightColor={highlightColor}
                    centered={true}
                />
            ) : (
                <StyledCarousel ref={ref}>
                    {items.map((post, i) => (
                        <SlideContent
                            key={`slice-${i}`}
                            {...post}
                            index={i}
                            itemsLength={items.length}
                            mirrored={mirrored}
                            highlightColor={highlightColor}
                            centered={items.length > 1 ? false : true}
                            arrowClickHandler={(index) => ref.current?.slideGoTo(index)}
                        />
                    ))}
                </StyledCarousel>
            )}
        </OuterWrapper>
    )
}

type SlideContentProps = Item & {
    index: number
    itemsLength: number
    mirrored?: boolean
    centered?: boolean
    highlightColor?: BackgroundColor
    arrowClickHandler?: (index: number) => void | null
}

const SlideContent = (props: SlideContentProps) => {
    const renderVisuals = () => {
        if (props.video) {
            return (
                <Video
                    poster={props.image?.url}
                    video={props.video}
                    posterWidth={600}
                    dimensions={props.image?.dimensions}
                />
            )
        } else if (props.image) {
            return <Image image={props.image} />
        } else {
            return null
        }
    }

    return (
        <StyledSlideContent mirrored={props.mirrored}>
            <StyledBackgroundWrapper $color={props.highlightColor} centered={props.centered}>
                <TextWrapper>
                    <TitleAndBodyWrapper>
                        <Text as="h2" $variant="title-base">
                            {props.title}
                        </Text>
                        <RichText content={props.body} />
                    </TitleAndBodyWrapper>
                    <Button $variant="outline" as={PrismicLink} {...props.ctaLink}>
                        {props.ctaText}
                    </Button>
                </TextWrapper>
                {props.itemsLength > 1 && (
                    <Pagination>
                        <div>
                            <Text as="span" $variant="paragraph-base">
                                {formatSlideCount(Math.max(1, props.index + 1))}
                            </Text>

                            <Text as="span" $variant="paragraph-base">
                                {' / '}
                            </Text>

                            <Text as="span" $variant="paragraph-base" $color="lightMuted">
                                {formatSlideCount(Math.max(1, props.itemsLength - 1 + 1))}
                            </Text>
                        </div>

                        <Arrows>
                            <Arrow
                                onClick={() =>
                                    props.arrowClickHandler &&
                                    props.arrowClickHandler(props.index - 1)
                                }
                                disabled={props.index <= 0}
                            >
                                <ArrowLeft />
                            </Arrow>

                            <Arrow
                                onClick={() =>
                                    props.arrowClickHandler &&
                                    props.arrowClickHandler(props.index + 1)
                                }
                                disabled={props.index + 1 >= props.itemsLength}
                            >
                                <ArrowRight />
                            </Arrow>
                        </Arrows>
                    </Pagination>
                )}
            </StyledBackgroundWrapper>
            <VisualsWrapper>{renderVisuals()}</VisualsWrapper>
        </StyledSlideContent>
    )
}

const formatSlideCount = (num: number) => num.toString().padStart(2, '0')

const StyledSlideContent = styled.div<{mirrored?: boolean}>(({mirrored}) => {
    const mirroredStyles = css`
        direction: rtl;

        > * {
            direction: initial;
        }
    `

    return css`
        display: grid;
        grid-template-rows: auto max-content;
        height: inherit;

        ${mq.min('sm')} {
            grid-template-columns: 1fr 1fr;
            grid-column-gap: ${tokens.spacing40};
        }

        ${mirrored && mirroredStyles}
    `
})

const StyledCarousel = styled(Carousel)`
    grid-gap: ${tokens.spacing24};
`

const StyledBackgroundWrapper = styled(BackgroundWrapper)<{centered?: boolean}>`
    display: grid;
    padding: ${tokens.spacing40};
    align-items: ${(props) => (props.centered ? 'center' : 'start')};
`

const TextWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing28};
    justify-items: start;
`

const TitleAndBodyWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};
`

const OuterWrapper = styled(Section)`
    display: grid;
    row-gap: ${tokens.spacing40};
    column-gap: ${tokens.spacing40};
    align-items: start;
`

const VisualsWrapper = styled.div`
    display: grid;
`

const Pagination = styled.div`
    display: grid;
    padding-top: ${tokens.spacing40};
    justify-content: space-between;
    align-self: end;
    grid-auto-flow: column;
`

const Arrows = styled.div`
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
    align-items: center;
    column-gap: ${tokens.spacing16};
`

const Arrow = styled.button`
    width: 20px;
    height: 20px;
    padding: unset;
    color: ${tokens.shade800};
    cursor: pointer;
    background: unset;
    border: unset;

    &:hover,
    &:focus-visible {
        color: ${tokens.shade900};
    }

    &:disabled {
        color: ${tokens.shade600};
        cursor: default;
    }

    svg {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`

export const highlightFragment = graphql`
    fragment Highlight on PrismicSlicePageDataBodyHighlight {
        __typename
        primary {
            title
            mirrored
            highlight_color
        }
        items {
            slide_title
            slide_body {
                richText
            }
            cta_text
            cta_link {
                ...PrismicLink
            }
            tracking_event
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
        }
    }
`
